"use client"

import { useEffect, useRef, useState } from "react"
import { Box, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { getCurrencyCodeFromCookie } from "@src/api/lib/currencyUtils"
import { setCookie } from "@src/api/lib/cookieUtils"
import { useFloatingActions } from "@src/components/FloatingActions/context"
import FlagComponent from "@src/components/FlagComponent"

type CurrencyType = {
  code: string
  flag: string
}

const currencies: CurrencyType[] = [
  { code: "VND", flag: "vn" },
  { code: "USD", flag: "us" },
  { code: "EUR", flag: "eu" },
  { code: "GBP", flag: "gb" },
  { code: "JPY", flag: "jp" },
  { code: "SGD", flag: "sg" },
  { code: "AUD", flag: "au" },
  { code: "CAD", flag: "ca" },
  { code: "HKD", flag: "hk" },
  { code: "KRW", flag: "kr" },
  { code: "CNY", flag: "cn" },
  { code: "CHF", flag: "ch" },
  { code: "INR", flag: "in" },
  { code: "BRL", flag: "br" },
  { code: "MXN", flag: "mx" },
  { code: "ZAR", flag: "za" }
]

const panelSx = {
  position: "absolute",
  right: "calc(100% + 1rem)",
  bottom: 0,
  width: "14rem",
  maxHeight: "min(24rem, calc(100vh - 2.4rem))",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: "1rem",
  bgcolor: "#fff",
  border: "1px solid #E8E8E8",
  boxShadow: "0 1.2rem 3.2rem rgba(0,0,0,0.16)",
  zIndex: 3
} as const

const listScrollSx = {
  py: "0.4rem",
  overflowY: "auto",
  flex: 1,
  minHeight: 0,
  scrollbarWidth: "thin",
  scrollbarColor: "#afafaf transparent",
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px"
  },
  "&::-webkit-scrollbar-track": {
    borderRadius: "20px",
    background: "transparent",
    marginBlock: "4px"
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#afafaf",
    borderRadius: "60px"
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#8A8A8A"
  }
} as const

const CurrencySelector = () => {
  const { i18n } = useTranslation()
  const { openPanel, togglePanel, setOpenPanel } = useFloatingActions()
  const rootRef = useRef<HTMLDivElement>(null)
  const open = openPanel === "currency"
  const [selectedCurrency, setSelectedCurrency] = useState(
    () =>
      currencies.find(c => c.code === getCurrencyCodeFromCookie()) ??
      currencies[0]
  )

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpenPanel(null)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenPanel(null)
    }

    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open, setOpenPanel])

  const selectCurrency = (currency: CurrencyType) => {
    setOpenPanel(null)
    if (currency.code === selectedCurrency.code) return
    setSelectedCurrency(currency)
    setCookie("NEXT_CURRENCY_CODE", currency.code, 7)
    window.location.reload()
  }

  const menuTitle = i18n.language === "en" ? "Currency" : "Tiền tệ"

  return (
    <Box ref={rootRef} sx={{ position: "relative", zIndex: 2 }}>
      <Box
        component="button"
        type="button"
        aria-label="Select currency"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => togglePanel("currency")}
        sx={{
          all: "unset",
          boxSizing: "border-box",
          cursor: "pointer",
          width: "4.4rem",
          height: "4.4rem",
          borderRadius: "50%",
          bgcolor: "#000",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.25rem",
          boxShadow: "0 0.25rem 0.7rem rgba(0,0,0,0.22)",
          transition: "transform 160ms ease, background-color 160ms ease",
          "&:hover": {
            bgcolor: "#141414",
            transform: "translateY(-1px)"
          },
          "&:focus-visible": {
            outline: "2px solid #fff",
            outlineOffset: "2px"
          }
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            lineHeight: 1,
            color: "inherit"
          }}
        >
          {selectedCurrency.code}
        </Typography>
      </Box>

      <Box
        role="listbox"
        aria-label="Currency options"
        aria-hidden={!open}
        sx={{
          ...panelSx,
          opacity: open ? 1 : 0,
          visibility: open ? "visible" : "hidden",
          transform: open ? "translateX(0)" : "translateX(0.5rem)",
          transition:
            "opacity 160ms ease, transform 160ms ease, visibility 160ms ease",
          pointerEvents: open ? "auto" : "none"
        }}
      >
        <Box
          sx={{
            px: "1.2rem",
            pt: "1rem",
            pb: "0.7rem",
            borderBottom: "1px solid #F0F0F0",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "0.8rem"
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: "0.95rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#8A8A8A",
              lineHeight: 1
            }}
          >
            {menuTitle}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label={i18n.language === "en" ? "Close" : "Đóng"}
            onClick={() => setOpenPanel(null)}
            sx={{
              all: "unset",
              boxSizing: "border-box",
              cursor: "pointer",
              width: "1.6rem",
              height: "1.6rem",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#B0B0B0",
              fontSize: "1.3rem",
              lineHeight: 1,
              transition: "background-color 120ms ease, color 120ms ease",
              "&:hover": {
                bgcolor: "#F0F0F0",
                color: "#141414"
              }
            }}
          >
            ×
          </Box>
        </Box>

        <Box sx={listScrollSx}>
          {currencies.map(currency => {
            const selected = currency.code === selectedCurrency.code
            return (
              <Box
                key={currency.code}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => selectCurrency(currency)}
                sx={{
                  all: "unset",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: "0.8rem",
                  px: "1.2rem",
                  py: "0.75rem",
                  bgcolor: selected ? "#000" : "transparent",
                  color: selected ? "#fff" : "#141414",
                  transition: "background-color 120ms ease",
                  "&:hover": {
                    bgcolor: selected ? "#000" : "#F6F6F6"
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    lineHeight: 0,
                    borderRadius: "2px",
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: selected
                      ? "inset 0 0 0 1px rgba(255,255,255,0.25)"
                      : "inset 0 0 0 1px rgba(0,0,0,0.08)"
                  }}
                >
                  <FlagComponent
                    code={currency.flag}
                    width={20}
                    height={15}
                  />
                </Box>
                <Typography
                  component="span"
                  sx={{
                    flex: 1,
                    fontSize: "1.2rem",
                    fontWeight: selected ? 600 : 500,
                    letterSpacing: "0.08em",
                    lineHeight: 1,
                    color: "inherit"
                  }}
                >
                  {currency.code}
                </Typography>
                {selected ? (
                  <Box
                    component="span"
                    sx={{
                      width: "0.5rem",
                      height: "0.5rem",
                      borderRadius: "50%",
                      bgcolor: "#fff"
                    }}
                  />
                ) : null}
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default CurrencySelector
